import axios from "axios"
import GeneralService from "./generalService"
import CvSkillDto from "../models/cvskill"

// 🔹 Interfaces pour les types de données (alignées avec le backend)
interface PolePersonnaliteTrait {
	id: number
	personnaliteTrait: string
}

interface PolePersonnaliteType {
	id: number
	personnaliteType: string
}

class CvSkillService extends GeneralService<CvSkillDto> {
	constructor() {
		super("cvskills")
	}

	// 🔹 Helper pour récupérer le token d'authentification
	private getAuthToken(): string | null {
		try {
			const userString = localStorage.getItem("user")
			const user = userString ? JSON.parse(userString) : null
			return user?.token || null
		} catch (error) {
			console.error(
				"[CvSkillService] ❌ Erreur récupération token:",
				error,
			)
			return null
		}
	}

	// 🔹 Helper pour créer les headers d'authentification
	private getAuthHeaders() {
		const token = this.getAuthToken()
		return token ? { Authorization: `Bearer ${token}` } : {}
	}

	// ========================
	// 📋 CRUD CvSkill
	// ========================

	/**
	 * Création d'un CvSkill pour un utilisateur donné
	 */
	createCvSkill(cvSkillDto: CvSkillDto, userId: number): Promise<CvSkillDto> {
		console.log(
			`[CvSkillService] 📝 Création CvSkill pour userId: ${userId}`,
		)
		return this.post(cvSkillDto, `/user/${userId}`)
	}

	/**
	 * Récupération de tous les CvSkills
	 */
	getAllCvSkills(): Promise<CvSkillDto[]> {
		console.log("[CvSkillService] 📋 Récupération de tous les CvSkills")
		return this.getAllWithFilters()
	}

	/**
	 * Récupération d'un CvSkill par ID
	 */
	getCvSkillById(id: number): Promise<CvSkillDto> {
		console.log(`[CvSkillService] 🔍 Récupération CvSkill ID: ${id}`)
		return this.getById(id)
	}

	/**
	 * Récupération des CvSkills d'un utilisateur
	 */
	getCvSkillsByUserId(userId: number): Promise<CvSkillDto> {
		console.log(
			`[CvSkillService] 👤 Récupération CvSkills pour userId: ${userId}`,
		)
		return this.get(`/user/${userId}`)
	}

	/**
	 * Mise à jour d'un CvSkill existant
	 */
	updateCvSkill(
		id: number,
		cvSkillDto: CvSkillDto,
		userId: number,
	): Promise<CvSkillDto> {
		console.log(
			`[CvSkillService] ✏️ Mise à jour CvSkill ID: ${id} pour userId: ${userId}`,
		)
		return this.put(cvSkillDto, `${id}?userId=${userId}`)
	}

	/**
	 * Suppression d'un CvSkill
	 */
	deleteCvSkill(id: number): Promise<CvSkillDto> {
		console.log(`[CvSkillService] 🗑️ Suppression CvSkill ID: ${id}`)
		return this.delete(id)
	}

	// ========================
	// 📸 Gestion des Photos
	// ========================

	/**
	 * Récupération de la photo liée à un CvSkill
	 */
	async getPhoto(cvSkillId: number): Promise<ArrayBuffer | null> {
		try {
			console.log(
				`[CvSkillService] 📸 Récupération photo pour CvSkill ID: ${cvSkillId}`,
			)

			const response = await axios.get<ArrayBuffer>(
				`${this.url}/${cvSkillId}/photo`,
				{
					responseType: "arraybuffer",
					headers: {
						Accept: "image/jpeg, image/png",
						...this.getAuthHeaders(),
					},
				},
			)

			console.log(`[CvSkillService] ✅ Photo récupérée avec succès`)
			return response.data
		} catch (error) {
			console.error(
				`[CvSkillService] ❌ Erreur récupération photo:`,
				error,
			)
			return null
		}
	}

	/**
	 * Upload d'une photo sur un CvSkill
	 */
	async uploadPhoto(cvSkillId: number, photo: File): Promise<void> {
		console.log(
			`[CvSkillService] 📤 Upload photo pour CvSkill ID: ${cvSkillId}`,
		)

		try {
			await this.uploadFile(photo, `/${cvSkillId}/photo`, {
				cvSkillId: cvSkillId.toString(),
			})
			console.log(`[CvSkillService] ✅ Photo uploadée avec succès`)
		} catch (error) {
			console.error(`[CvSkillService] ❌ Erreur upload photo:`, error)
			throw error
		}
	}

	// ========================
	// 🎭 Pôle Personnalité
	// ========================

	/**
	 * Récupération de la liste des traits de personnalité depuis le backend
	 * Endpoint: GET /api/personnalite-traits
	 * Controller: PolePersonnaliteTraitController
	 */
	async getPersonnaliteTraits(): Promise<PolePersonnaliteTrait[]> {
		try {
			console.log(
				"[CvSkillService] 🎭 Récupération traits de personnalité",
			)

			const response = await axios.get<PolePersonnaliteTrait[]>(
				`${process.env.REACT_APP_API_URL}/personnalite-traits`,
				{
					headers: this.getAuthHeaders(),
				},
			)

			console.log(
				`[CvSkillService] ✅ ${response.data.length} traits récupérés:`,
			)
			response.data.forEach(trait => {
				console.log(
					`[CvSkillService]   -> ID: ${trait.id} | Trait: ${trait.personnaliteTrait}`,
				)
			})

			return response.data
		} catch (error) {
			console.error(
				"[CvSkillService] ❌ Erreur chargement traits:",
				error,
			)
			throw new Error("Impossible de charger les traits de personnalité.")
		}
	}

	/**
	 * Récupération de la liste des types de personnalité depuis le backend
	 * Endpoint: GET /api/personnalite-types
	 * Controller: PolePersonnaliteTypeController
	 */
	async getPersonnaliteTypes(): Promise<PolePersonnaliteType[]> {
		try {
			console.log(
				"[CvSkillService] 🎭 Récupération types de personnalité",
			)

			const response = await axios.get<PolePersonnaliteType[]>(
				`${process.env.REACT_APP_API_URL}/personnalite-types`,
				{
					headers: this.getAuthHeaders(),
				},
			)

			response.data.forEach(type => {
				console.log(
					`[CvSkillService]   -> ID: ${type.id} | Type: ${type.personnaliteType}`,
				)
			})

			return response.data
		} catch (error) {
			console.error("[CvSkillService] ❌ Erreur chargement types:", error)
			throw new Error("Impossible de charger les types de personnalité.")
		}
	}
}

// Export singleton
const cvSkillService = new CvSkillService()
export default cvSkillService
